import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Image,
  VStack,
  HStack,
  SimpleGrid,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import ModernNavbar from '../component/header/ModernNavbar';

// Create a proper motion wrapper that doesn't pass motion props to DOM
const MotionWrapper = ({ children, ...motionProps }) => {
  return (
    <motion.div {...motionProps}>
      {children}
    </motion.div>
  );
};

const ModernAboutUs = () => {
  const bgColor = useColorModeValue('white', 'neutral.900');
  const textColor = useColorModeValue('neutral.900', 'white');
  const cardBg = useColorModeValue('white', 'neutral.800');

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <Box>
      <ModernNavbar />
      
      {/* Hero Section */}
      <Box
        bgImage="url('/assets/images/singha/floor1/IMG_7801.webp')"
        bgSize="cover"
        bgPosition="center"
        bgRepeat="no-repeat"
        position="relative"
        py={20}
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          w="100%"
          h="100%"
          bg="rgba(36, 36, 36, 0.5)"
        />
        <Container maxW="1200px" position="relative" zIndex={2}>
          <MotionWrapper
           
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.8 }}
          >
            <VStack spacing={6} textAlign="center" color="white">
              <Heading
                as="h1"
                size="2xl"
                fontFamily="heading"
                fontWeight="normal"
              >
                เกี่ยวกับเรา
              </Heading>
              <Text fontSize="xl" maxW="800px" lineHeight="1.6">
                สำรวจประวัติศาสตร์และวัฒนธรรมอันยาวนานของชุมชนบ้านสิงห์ท่า 
                ผ่านนิทรรศการที่บอกเล่าเรื่องราวของชุมชนและวิถีชีวิตท้องถิ่น
              </Text>
            </VStack>
          </MotionWrapper>
        </Container>
      </Box>

      {/* Museum History Section */}
      <Box py={20} bg={bgColor} id="museum-history">
        <Container maxW="1200px">
          <MotionWrapper
           
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
              <VStack align="flex-start" spacing={6}>
                <Heading
                  as="h2"
                  size="xl"
                  fontFamily="heading"
                  color={textColor}
                >
                  ประวัติพิพิธภัณฑ์
                </Heading>
                <Text fontSize="lg" color={textColor} lineHeight="1.8">
                  พิพิธภัณฑ์สิงห์ท่า ก่อตั้งขึ้นเพื่อเป็นศูนย์กลางการเรียนรู้ 
                  และอนุรักษ์ประวัติศาสตร์วัฒนธรรมของชุมชนบ้านสิงห์ท่า 
                  โดยจัดแสดงนิทรรศการที่บอกเล่าเรื่องราวความเป็นมาของชุมชน 
                  ตั้งแต่ยุคก่อบ้านตั้งเมืองจนถึงปัจจุบัน
                </Text>
                <Text fontSize="lg" color={textColor} lineHeight="1.8">
                  พิพิธภัณฑ์แห่งนี้เป็นแหล่งเรียนรู้ที่สำคัญสำหรับนักเรียน นักศึกษา 
                  และประชาชนทั่วไปที่สนใจในประวัติศาสตร์และวัฒนธรรมท้องถิ่น 
                  โดยมีการจัดแสดงทั้งในรูปแบบนิทรรศการถาวรและหมุนเวียน
                </Text>
              </VStack>
              <Box>
                <Image
                  src="/assets/images/singha/floor1/IMG_7802.webp"
                  alt="พิพิธภัณฑ์สิงห์ท่า"
                  borderRadius="lg"
                  boxShadow="lg"
                />
              </Box>
            </SimpleGrid>
          </MotionWrapper>
        </Container>
      </Box>

      {/* Singha History Section */}
      <Box py={20} bg="neutral.50" id="singha-history">
        <Container maxW="1200px">
          <MotionWrapper
           
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
              <Box order={{ base: 1, lg: 2 }}>
                <Image
                  src="/assets/images/singha/floor1/IMG_7809.webp"
                  alt="ประวัติสิงหะ"
                  borderRadius="lg"
                  boxShadow="lg"
                />
              </Box>
              <VStack align="flex-start" spacing={6} order={{ base: 2, lg: 1 }}>
                <Heading
                  as="h2"
                  size="xl"
                  fontFamily="heading"
                  color="neutral.900"
                >
                  ประวัติสิงหะ
                </Heading>
                <Text fontSize="lg" color="neutral.700" lineHeight="1.8">
                  ชื่อ "สิงห์ท่า" มีที่มาจากตำนานสิงห์คู่ที่อาศัยอยู่ในบริเวณนี้ 
                  ซึ่งเป็นสัญลักษณ์ของความแข็งแกร่งและความอุดมสมบูรณ์ของชุมชน 
                  ตำนานนี้ถูกถ่ายทอดจากรุ่นสู่รุ่นและกลายเป็นส่วนสำคัญของอัตลักษณ์ชุมชน
                </Text>
                <Text fontSize="lg" color="neutral.700" lineHeight="1.8">
                  ในปี พ.ศ. 2357 บ้านสิงห์ท่าถูกยกขึ้นเป็นเมืองยศสุนทร 
                  และต่อมากลายเป็นจังหวัดยโสธร จังหวัดที่ 71 ของประเทศไทย 
                  ประวัติศาสตร์อันยาวนานนี้ถูกเก็บรักษาและนำเสนอผ่านนิทรรศการในพิพิธภัณฑ์
                </Text>
              </VStack>
            </SimpleGrid>
          </MotionWrapper>
        </Container>
      </Box>

      {/* Mission & Vision */}
      <Box py={20} bg={bgColor}>
        <Container maxW="1200px">
          <MotionWrapper
           
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <VStack spacing={12}>
              <VStack spacing={4} textAlign="center">
                <Heading
                  as="h2"
                  size="xl"
                  fontFamily="heading"
                  color={textColor}
                >
                  ภารกิจและวิสัยทัศน์
                </Heading>
                <Text fontSize="lg" color={textColor} maxW="600px">
                  พิพิธภัณฑ์สิงห์ท่ามุ่งมั่นที่จะเป็นศูนย์กลางการเรียนรู้ 
                  และอนุรักษ์ประวัติศาสตร์วัฒนธรรมของชุมชน
                </Text>
              </VStack>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                <Box
                  bg={cardBg}
                  p={8}
                  borderRadius="lg"
                  boxShadow="md"
                  textAlign="center"
                >
                  <Heading as="h3" size="lg" mb={4} fontFamily="heading" color={textColor}>
                    ภารกิจ
                  </Heading>
                  <Text color={textColor} lineHeight="1.6">
                    รวบรวม อนุรักษ์ และเผยแพร่ประวัติศาสตร์วัฒนธรรมของชุมชนบ้านสิงห์ท่า 
                    เพื่อให้เป็นแหล่งเรียนรู้ที่สำคัญสำหรับคนรุ่นปัจจุบันและอนาคต
                  </Text>
                </Box>
                <Box
                  bg={cardBg}
                  p={8}
                  borderRadius="lg"
                  boxShadow="md"
                  textAlign="center"
                >
                  <Heading as="h3" size="lg" mb={4} fontFamily="heading" color={textColor}>
                    วิสัยทัศน์
                  </Heading>
                  <Text color={textColor} lineHeight="1.6">
                    เป็นพิพิธภัณฑ์ชั้นนำในการอนุรักษ์และเผยแพร่ประวัติศาสตร์วัฒนธรรมท้องถิ่น 
                    และเป็นศูนย์กลางการเรียนรู้ที่ทันสมัยสำหรับทุกคน
                  </Text>
                </Box>
              </SimpleGrid>
            </VStack>
          </MotionWrapper>
        </Container>
      </Box>

      {/* Contact Information */}
      <Box py={20} bg="neutral.50">
        <Container maxW="1200px">
          <MotionWrapper
           
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <VStack spacing={8} textAlign="center">
              <Heading
                as="h2"
                size="xl"
                fontFamily="heading"
                color="neutral.900"
              >
                ติดต่อเรา
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="100%">
                <VStack spacing={4}>
                  <Heading as="h3" size="md" fontFamily="heading" color="neutral.900">
                    ที่อยู่
                  </Heading>
                  <Text color="neutral.700" lineHeight="1.6">
                    พิพิธภัณฑ์สิงห์ท่า<br />
                    ตำบลสิงห์ท่า<br />
                    อำเภอเมืองยโสธร<br />
                    จังหวัดยโสธร 35000
                  </Text>
                </VStack>
                <VStack spacing={4}>
                  <Heading as="h3" size="md" fontFamily="heading" color="neutral.900">
                    เวลาเปิดทำการ
                  </Heading>
                  <Text color="neutral.700" lineHeight="1.6">
                    วันอังคาร - วันอาทิตย์<br />
                    เวลา 09:00 - 16:00 น.<br />
                    ปิดทำการวันจันทร์
                  </Text>
                </VStack>
                <VStack spacing={4}>
                  <Heading as="h3" size="md" fontFamily="heading" color="neutral.900">
                    ติดต่อ
                  </Heading>
                  <Text color="neutral.700" lineHeight="1.6">
                    โทร: 045-123456<br />
                    อีเมล: info@singhathamuseum.com<br />
                    เว็บไซต์: www.singhathamuseum.com
                  </Text>
                </VStack>
              </SimpleGrid>
            </VStack>
          </MotionWrapper>
        </Container>
      </Box>
    </Box>
  );
};

export default ModernAboutUs; 